import { parsePhoneNumber } from 'libphonenumber-js';
import moment from 'moment';
import strings from 'strings';
import { DateFormats, DEFAULT_COUNTRY_CODE, MONTHS } from 'UI/constants/defaults';
import { currencyFormatter, formatInternationalPhone } from 'UI/utils';

import { LINKEDIN_HOSTNAME } from './ResumeCard.constants';

const formatDate = (dateObj, isCurrent = false) => {
  if (isCurrent) return strings.resumeSearch.resumeCard.currentYear;
  if (!dateObj) return '';

  if (dateObj.month && dateObj.year) {
    const month = MONTHS.find(m => m.id === dateObj.month);
    return `${month?.title || ''} ${dateObj.year}`;
  }
  if (dateObj.year) {
    return `${dateObj.year}`;
  }
  return '';
};

export const getPeriodString = (startDate, endDate, isCurrent = false) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate, isCurrent);
  return [start, end].filter(Boolean).join(' - ');
};

export const formatEducationPeriod = (startYear, endYear) => {
  if (startYear && endYear) {
    return `${startYear} - ${endYear}`;
  }
  return endYear || startYear || '';
};

export const joinWithBullet = (...parts) => parts.filter(Boolean).join(' • ');

export const formatPluralMessage = (message, count) => {
  const pluralSuffix = count > 1 ? 's' : '';
  return message.replace('{count}', count).replace('{plural}', pluralSuffix);
};

export const formatDescription = text => text || '';

export const extractLocation = (basics = {}) => {
  const { city_title: city, state_slug: state, zip_title: zip } = basics.address_components || {};
  const cityState = [city, state].filter(Boolean).join(', ');
  return [cityState, zip].filter(Boolean).join(' ');
};

export const extractFullName = (basics = {}) => {
  if (basics.full_name) return basics.full_name.trim();
  const firstName = basics.first_name || '';
  const lastName = basics.last_name || '';
  return `${firstName} ${lastName}`.trim();
};

export const extractProfession = (basics = {}) => basics.profession || '';

export const extractExperiences = (profile = {}) => profile.professional_experiences || [];

export const extractEducations = (profile = {}) => profile.educations || [];

export const extractCertifications = (profile = {}) => profile.certifications_and_licenses || [];

export const extractAssociations = (profile = {}) =>
  profile.professional_associations_and_memberships || [];

export const extractSkills = (profile = {}) => profile.skills || [];

export const extractPhoneNumbers = (basics = {}) => basics.phone_numbers || [];

export const extractPhoneDetails = (basics = {}) => basics.phone_details || [];

export const extractEmails = (basics = {}) => basics.emails || [];

export const extractUrls = (basics = {}) => basics.urls || [];

const isLinkedInUrl = url => {
  if (!url) return false;
  try {
    const { hostname } = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
    return hostname === LINKEDIN_HOSTNAME || hostname.endsWith(`.${LINKEDIN_HOSTNAME}`);
  } catch {
    return false;
  }
};

export const filterLinkedInUrls = (urls = []) => urls.filter(isLinkedInUrl);

export const formatPhoneDisplay = number => {
  if (!number) return number;

  const trimmed = number.trim();
  if (trimmed.startsWith('+')) return formatInternationalPhone(trimmed) || number;

  const digits = trimmed.replace(/\D/g, '');
  const withCountryCode = digits.length === 10 ? `1${digits}` : digits;
  return formatInternationalPhone(`+${withCountryCode}`) || number;
};

export const extractNationalNumber = phone =>
  (phone || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');

// The message-center drawer expects the national number and its country code separately
// (it prepends the code, defaulting to +1). Splitting here keeps non-NANP numbers intact
// instead of mangling e.g. +44... into +144....
export const splitInternationalPhone = phone => {
  try {
    const parsed = parsePhoneNumber(String(phone ?? '').trim());
    return { countryCode: `+${parsed.countryCallingCode}`, nationalNumber: parsed.nationalNumber };
  } catch (error) {
    return { countryCode: DEFAULT_COUNTRY_CODE, nationalNumber: extractNationalNumber(phone) };
  }
};

export const formatPhoneLabel = ({ use } = {}, index) => {
  if (use) {
    return strings.formatString(strings.resumeSearch.resumeCard.contactInfo.phoneWithUse, {
      use
    });
  }
  return index === 0
    ? strings.resumeSearch.resumeCard.contactInfo.phone
    : strings.formatString(strings.resumeSearch.resumeCard.contactInfo.phoneOrdinal, {
        count: index + 1
      });
};

export const formatEmailLabel = index =>
  index === 0
    ? strings.resumeSearch.resumeCard.contactInfo.email
    : strings.resumeSearch.resumeCard.contactInfo.altEmail;

export const getTypeColor = (type, colors = {}) => colors[type];

export const getStatusColor = (status, colors = {}) => colors[status];

export const formatMetadataDate = isoDate => {
  if (!isoDate) {
    return '—';
  }

  const date = moment(isoDate);

  if (!date.isValid()) {
    return '—';
  }

  return date.format(DateFormats.MonthDayYear);
};

export const extractLastActivityDetail = resume => resume?.candidate?.lastActivity || null;

export const formatLastActivityLine = (lastActivityDetail = null) => {
  if (!lastActivityDetail?.type && !lastActivityDetail?.date) return '';
  const { type, date } = lastActivityDetail;

  return [type, date && formatMetadataDate(date)].filter(Boolean).join(', ');
};

export const formatCreatedAtLine = createdAt => {
  if (!createdAt) return '';
  const formatted = formatMetadataDate(createdAt);
  return formatted === '—' ? '' : formatted;
};

export const extractCandidateType = resume => resume?.candidate?.type || null;

export const extractCandidateStatus = resume => resume?.candidate?.status || null;

export const extractCreatedAt = resume => resume?.meta?.file?.uploadedAt || null;

export const extractLastActivityDate = resume => resume?.candidate?.last_activity_date || null;

const extractFunctionalTitle = resume => {
  return resume?.candidate?.functionalTitle?.title || '';
};

const extractIndustry = resume => {
  return resume?.candidate?.industry?.title || '';
};

const extractSpecialty = resume => {
  return resume?.candidate?.specialty?.title || '';
};

const extractSubSpecialty = resume => {
  return resume?.candidate?.subspecialty?.title || '';
};

export const extractCurrentCompany = resume => resume?.candidate?.currentCompany || '';

export const extractYearsExperience = (basics = {}) =>
  typeof basics.total_experience_in_years === 'number' ? basics.total_experience_in_years : null;

export const extractIdealTimeToStart = resume => resume?.candidate?.urgency?.idealTimeToStart || '';

export const extractWorkPreferences = resume => resume?.candidate?.workPreferences || null;

export const extractTeamName = resume => resume?.candidate?.teamName || '';

export const extractEmailStatus = resume => resume?.candidate?.emailStatus || null;

const hasSalaryValue = value => value !== null && value !== undefined && value !== '';

const formatSalaryDisplay = (salaryRange = {}) => {
  const range = salaryRange || {};

  if (hasSalaryValue(range.idealSalary)) {
    return currencyFormatter(range.idealSalary, 0);
  }

  if (hasSalaryValue(range.lowSalary) && hasSalaryValue(range.highSalary)) {
    return `${currencyFormatter(range.lowSalary, 0)}–${currencyFormatter(range.highSalary, 0)}`;
  }

  return '';
};

const formatRelocationLabel = relocation => {
  if (!relocation) return '';
  const yesNo =
    {
      yes: strings.shared.ui.yes,
      no: strings.shared.ui.no
    }[typeof relocation === 'string' ? relocation.trim().toLowerCase() : ''] || relocation;
  return `${strings.resumeSearch.resumeCard.relocateLabel}: ${yesNo}`;
};

export const formatWorkPreferencesLine = (workPreferences = null, idealTimeToStart = '') => {
  if (!workPreferences && !idealTimeToStart) return '';
  const { salaryRange, workPreference, relocation } = workPreferences || {};

  const startAvailabilityText = idealTimeToStart
    ? strings.formatString(strings.resumeSearch.resumeCard.startAvailability, {
        value: idealTimeToStart
      })
    : '';

  return joinWithBullet(
    salaryRange ? formatSalaryDisplay(salaryRange) : '',
    workPreference || '',
    formatRelocationLabel(relocation),
    startAvailabilityText
  );
};

const tokenize = value =>
  typeof value === 'string'
    ? value
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .map(token => token.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, ''))
        .filter(Boolean)
    : [];

const professionContainsFunctionalTitle = (profession, functionalTitle) => {
  const professionTokens = tokenize(profession);
  const titleTokens = tokenize(functionalTitle);
  if (!titleTokens.length) return false;

  return professionTokens.some((_, index) =>
    titleTokens.every((token, offset) => professionTokens[index + offset] === token)
  );
};

const formatRoleAndCompany = (role, currentCompany) => {
  if (role && currentCompany) {
    return strings.formatString(strings.resumeSearch.resumeCard.roleAtCompany, {
      role,
      company: currentCompany
    });
  }
  return role || currentCompany;
};

export const formatTier1Line = ({
  profession = '',
  functionalTitle = '',
  currentCompany = '',
  yearsExperience = null
} = {}) => {
  const shouldOmitFunctionalTitle =
    profession && functionalTitle && professionContainsFunctionalTitle(profession, functionalTitle);
  const role = joinWithBullet(profession, shouldOmitFunctionalTitle ? '' : functionalTitle);
  const roleAndCompany = formatRoleAndCompany(role, currentCompany);

  const yearsExperienceText =
    typeof yearsExperience === 'number'
      ? strings.formatString(strings.resumeSearch.resumeCard.yearsExperienceSuffix, {
          count: yearsExperience
        })
      : '';

  return joinWithBullet(roleAndCompany, yearsExperienceText);
};

export const extractResumeData = (resume = {}) => {
  const profile = resume.profile || {};
  const basics = profile.basics || {};
  const candidateType = extractCandidateType(resume);
  const candidateStatus = extractCandidateStatus(resume);

  return {
    fullName: extractFullName(basics),
    profession: extractProfession(basics),
    functionalTitle: extractFunctionalTitle(resume),
    industry: extractIndustry(resume),
    specialty: extractSpecialty(resume),
    subSpecialty: extractSubSpecialty(resume),
    currentCompany: extractCurrentCompany(resume),
    yearsExperience: extractYearsExperience(basics),
    idealTimeToStart: extractIdealTimeToStart(resume),
    workPreferences: extractWorkPreferences(resume),
    teamName: extractTeamName(resume),
    emailStatus: extractEmailStatus(resume),
    location: extractLocation(basics),
    experiences: extractExperiences(profile),
    educations: extractEducations(profile),
    certifications: extractCertifications(profile),
    associations: extractAssociations(profile),
    skills: extractSkills(profile),
    phoneNumbers: extractPhoneNumbers(basics),
    phoneDetails: extractPhoneDetails(basics),
    emails: extractEmails(basics),
    urls: extractUrls(basics),
    candidateType: candidateType ? { id: candidateType.id, title: candidateType.title } : null,
    candidateStatus: candidateStatus
      ? { id: candidateStatus.id, title: candidateStatus.title }
      : null,
    createdAt: extractCreatedAt(resume),
    lastActivityDate: extractLastActivityDate(resume),
    lastActivityDetail: extractLastActivityDetail(resume)
  };
};

export const getExpandButtonText = ({ items = [] }) => {
  return strings.formatString(strings.resumeSearch.resumeCard.showAll, {
    count: items.length
  });
};
